import React from "react";
import { Card } from "./ui/card";
import { CheckCircle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";

const CorporateWellness = () => {
  const navigate = useNavigate();
  const handleBookAppointment = () => navigate("/book-appointment");

  const services = [
    "Workplace wellness workshops",
    "Ergonomic assessments",
    "Employee support programs",
    "Stress management & mental health sessions",
  ];

  const benefits = [
    "Improved employee productivity & engagement",
    "Reduced absenteeism & burnout",
    "Better workplace morale",
    "Customizable programs for any organization size",
  ];

  return (
    <>
      <Header hideBookButton={true} />
      <section className="py-20 bg-gradient-to-br from-white via-slate-50 to-blue-50 relative overflow-hidden min-h-screen">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#204080]/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#2E5AAC]/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-[#204080]/10 text-[#204080] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span>Corporate Wellness</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-[#204080] mb-6 font-serif">
              Wellness That Works for Your Workplace
              <span className="block bg-clip-text bg-gradient-to-r from-[#204080] to-[#2E5AAC]">
                Health & Productivity
              </span>
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our corporate wellness programs are designed to keep your employees
              healthier, happier, and more engaged. By focusing on preventive
              health, ergonomic work environments, and stress management, we help
              organizations build a culture where employees can thrive.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-16 max-w-3xl mx-auto">
            {services.map((service, idx) => (
              <Card
                key={idx}
                className="flex items-center gap-4 p-6 bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <CheckCircle size={24} className="text-[#204080]" />
                <p className="text-gray-700 font-medium">{service}</p>
              </Card>
            ))}
          </div>

          {/* Benefits Section */}
          <Card className="max-w-3xl mx-auto bg-blue-50 p-8 rounded-lg mb-16">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">Why Partner With Us?</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 text-lg">
              {benefits.map((benefit, idx) => (
                <li key={idx}>{benefit}</li>
              ))}
            </ul>
          </Card>

          {/* Success Metrics */}
          <Card className="p-8 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white border-0 shadow-2xl mb-16 max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-4">Wellness Program Outcomes</h3>
              <p className="text-blue-100 text-lg">Creating healthier, happier workplaces</p>
            </div>

            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-4xl font-bold mb-2">80%</div>
                <div className="text-blue-100 text-sm">Increased Employee Engagement</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">70%</div>
                <div className="text-blue-100 text-sm">Reduced Sick Leave</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">65%</div>
                <div className="text-blue-100 text-sm">Improved Mental Wellness</div>
              </div>
              <div>
                <div className="text-4xl font-bold mb-2">100+</div>
                <div className="text-blue-100 text-sm">Organizations Served</div>
              </div>
            </div>
          </Card>

          {/* CTA */}
          <div className="text-center">
            <Card className="max-w-4xl mx-auto p-8 bg-white/90 backdrop-blur-sm border border-gray-200 shadow-2xl">
              <h3 className="text-2xl font-bold text-[#204080] mb-4">
                Bring Wellness to Your Workplace
              </h3>
              <p className="text-gray-600 mb-8 text-lg">
                Empower your workforce with our tailored wellness solutions.
              </p>
              <div className="flex justify-center">
                <button
                  className="flex items-center justify-center space-x-2 px-8 py-3 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  onClick={() => navigate('/book-appointment', { state: { selectedService: 'Corporate Wellness' } })}
                >
                  <span>Start Corporate Wellness</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </>
  );
};

export default CorporateWellness;
